/* =============================================================================
 * SATURATE — Theme behavior
 * Reveal-on-scroll, header scroll state, cart drawer trigger, mobile nav,
 * and any other custom-element registrations we own (Dawn's primitives are
 * loaded separately and we don't override them).
 * ============================================================================= */

(function () {
  'use strict';

  // -------------------- Reveal-on-scroll --------------------
  // Single fade-and-rise per element (design-language.md §5). 880ms, 24px translate.
  function initReveal() {
    const els = document.querySelectorAll('.reveal');
    if (!els.length) return;

    if (!('IntersectionObserver' in window)) {
      els.forEach((el) => el.classList.add('in-view'));
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('in-view');
            io.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.01 }
    );

    els.forEach((el) => io.observe(el));
  }

  // -------------------- Header scroll state --------------------
  // Adds .is-scrolled to <header> once the user has scrolled past 8px so the
  // bottom hairline can sharpen and the announce-strip can collapse if desired.
  function initHeaderScroll() {
    const header = document.querySelector('[data-saturate-header]');
    if (!header) return;
    let ticking = false;
    const update = () => {
      header.classList.toggle('is-scrolled', window.scrollY > 8);
      ticking = false;
    };
    window.addEventListener(
      'scroll',
      () => {
        if (!ticking) {
          window.requestAnimationFrame(update);
          ticking = true;
        }
      },
      { passive: true }
    );
    update();
  }

  // -------------------- Cart drawer triggers --------------------
  // Any element with [data-open-cart-drawer] opens the Dawn cart-drawer custom element.
  function initCartTriggers() {
    document.addEventListener('click', (e) => {
      const trigger = e.target.closest('[data-open-cart-drawer]');
      if (!trigger) return;
      const drawer = document.querySelector('cart-drawer');
      if (!drawer || typeof drawer.open !== 'function') return;
      e.preventDefault();
      drawer.open(trigger);
    });
  }

  // -------------------- Cart count badge sync --------------------
  // Dawn publishes PUB_SUB_EVENTS.cartUpdate (string 'cart-update') after a successful
  // /cart/add.js or /cart/change.js. We hook the same channel and refresh the
  // [data-cart-count] badges (desktop + mobile header) so the count is correct
  // without a page reload. Dawn's own cart-icon-bubble rerender targets a
  // #cart-icon-bubble element that the Saturate header doesn't render.
  function initCartCountSync() {
    const refresh = () =>
      fetch('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
        .then((r) => r.json())
        .then((cart) => {
          document.querySelectorAll('[data-cart-count]').forEach((el) => {
            el.textContent = cart.item_count;
          });
        })
        .catch(() => {});

    // pubsub.js exposes a global `subscribe()` and `PUB_SUB_EVENTS`. They're
    // defined after our script runs (defer order), so guard until present.
    const wire = () => {
      if (typeof window.subscribe === 'function' && window.PUB_SUB_EVENTS) {
        window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, refresh);
        window.subscribe(window.PUB_SUB_EVENTS.cartError, refresh);
      } else {
        setTimeout(wire, 100);
      }
    };
    wire();
  }

  // -------------------- Mobile nav toggle --------------------
  function initMobileNav() {
    const toggle = document.querySelector('[data-mobile-nav-toggle]');
    const drawer = document.querySelector('[data-mobile-nav]');
    if (!toggle || !drawer) return;

    const setOpen = (open) => {
      // Toggle both [data-open] (CSS hook) and [hidden] (a11y semantics) in lock-step
      // so assistive tech and visual state stay in sync.
      drawer.toggleAttribute('data-open', open);
      drawer.toggleAttribute('hidden', !open);
      toggle.setAttribute('aria-expanded', String(open));
      document.documentElement.style.overflow = open ? 'hidden' : '';
    };

    toggle.addEventListener('click', () => {
      setOpen(!drawer.hasAttribute('data-open'));
    });

    drawer.addEventListener('click', (e) => {
      if (e.target.matches('[data-mobile-nav-close], a')) setOpen(false);
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && drawer.hasAttribute('data-open')) setOpen(false);
    });
  }

  // -------------------- FAQ accordion --------------------
  // Simple click-to-toggle. Multiple items can be open simultaneously
  // (tools-and-filtering.md §4 — no exclusive-open).
  function initFaqAccordion() {
    document.querySelectorAll('[data-faq-item]').forEach((item, index) => {
      const btn = item.querySelector('[data-faq-q]');
      const panel = item.querySelector('[data-faq-a]');
      const toggle = item.querySelector('[data-faq-toggle]');
      if (!btn || !panel) return;

      // First item open by default.
      const startOpen = index === 0;
      if (startOpen) {
        item.classList.add('is-open');
        panel.removeAttribute('hidden');
        if (toggle) toggle.textContent = '−';
        btn.setAttribute('aria-expanded', 'true');
      } else {
        panel.setAttribute('hidden', '');
        btn.setAttribute('aria-expanded', 'false');
      }

      btn.addEventListener('click', () => {
        const open = item.classList.toggle('is-open');
        panel.toggleAttribute('hidden', !open);
        btn.setAttribute('aria-expanded', String(open));
        if (toggle) toggle.textContent = open ? '−' : '+';
      });
    });
  }

  // -------------------- Spec drawer (PDP) --------------------
  // <details> elements get smooth height animation. The chevron is text-based (+/−)
  // not an icon (brand register prohibits rotating chevrons).
  function initSpecDrawers() {
    document.querySelectorAll('[data-spec-drawer]').forEach((d) => {
      const summary = d.querySelector('summary');
      const toggle = d.querySelector('[data-spec-toggle]');
      if (!summary) return;
      d.addEventListener('toggle', () => {
        if (toggle) toggle.textContent = d.open ? '−' : '+';
      });
    });
  }

  // -------------------- Boot --------------------
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  function boot() {
    initReveal();
    initHeaderScroll();
    initCartTriggers();
    initCartCountSync();
    initMobileNav();
    initFaqAccordion();
    initSpecDrawers();
  }
})();
